import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BedSingle, Copy, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { getCurrencySymbol } from "@/lib/currencyUtils";
import { formatEquivalents } from "@/lib/currencyConversion";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import {
  applySupplementMarkup,
  buildSingleSupplementLines,
  isSupplementEligible,
  sumSupplementLines,
  type SupplementSourceItem,
} from "@/lib/singleSupplementUtils";

interface Props {
  leadId: string;
  items: SupplementSourceItem[];
  currency?: string;
  markupPercent?: number;
  savedAmount?: number | null;
  disabled?: boolean;
}

export function SingleSupplementDialog({
  leadId,
  items,
  currency = "ZAR",
  markupPercent = 0,
  savedAmount = null,
  disabled = false,
}: Props) {
  const queryClient = useQueryClient();
  const { data: exchangeRates } = useExchangeRates();
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState("50");
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const symbol = getCurrencySymbol(currency);
  const eligibleItems = useMemo(() => items.filter(isSupplementEligible), [items]);

  useEffect(() => {
    if (!open) setOverrides({});
  }, [open]);

  const lines = useMemo(
    () =>
      buildSingleSupplementLines(eligibleItems, {
        percent: parseFloat(percent) || 0,
      }),
    [eligibleItems, percent]
  );

  // Each line's marked-up figure, unless the user typed over it.
  const lineTotals = lines.map((line) => {
    const override = overrides[line.itemId];
    const marked = applySupplementMarkup(line.total, markupPercent);
    const value = override !== undefined && override !== "" ? parseFloat(override) : marked;
    return { line, marked, value: isNaN(value) ? 0 : value };
  });

  const netTotal = sumSupplementLines(lines);
  const grandTotal =
    Math.round(lineTotals.reduce((sum, l) => sum + l.value, 0) * 100) / 100;

  const format = (v: number) =>
    `${symbol}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(grandTotal.toFixed(2));
      toast.success("Single supplement copied");
    } catch {
      toast.error("Could not copy — please select the figure manually");
    }
  };

  const persist = async (amount: number | null) => {
    setSaving(true);
    const { error } = await supabase
      .from("leads")
      .update({ single_supplement_amount: amount } as any)
      .eq("id", leadId);
    setSaving(false);
    if (error) {
      toast.error("Could not save the single supplement");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    queryClient.invalidateQueries({ queryKey: ["lead"] });
    toast.success(amount === null ? "Single supplement removed" : "Single supplement saved to quote");
    setOpen(false);
  };

  if (!eligibleItems.length) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" disabled={disabled}>
          <BedSingle className="h-4 w-4" />
          Single supplement
          {savedAmount != null && savedAmount > 0 && (
            <Badge variant="secondary" className="ml-1">{format(Number(savedAmount))}</Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Single supplement</DialogTitle>
          <DialogDescription>
            What one traveller pays extra for a room to themselves across the whole programme.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="supplement-percent" className="text-xs">
                Supplement percentage — % of the room rate
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="supplement-percent"
                  type="number"
                  min={0}
                  max={200}
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  className="h-8 w-24"
                />
                <span className="text-sm text-muted-foreground">% of the room rate</span>
              </div>
            </div>
          </div>

          <div className="max-h-[45vh] overflow-y-auto space-y-2">
            {lineTotals.map(({ line, marked, value }) => (
              <div
                key={line.itemId}
                className="flex items-center gap-2 p-2 rounded border bg-background text-xs"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{line.label}</div>
                  <div className="text-muted-foreground">
                    {line.nights} {line.nights === 1 ? "night" : "nights"} × {format(line.perNight)}
                    /night
                  </div>
                </div>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={overrides[line.itemId] ?? marked.toFixed(2)}
                  onChange={(e) =>
                    setOverrides((prev) => ({ ...prev, [line.itemId]: e.target.value }))
                  }
                  className="h-8 w-28 text-right"
                />
                {value !== marked && (
                  <span className="text-primary text-[10px] w-10">edited</span>
                )}
              </div>
            ))}
          </div>

          <div className="p-3 rounded-lg border bg-muted/40 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Net (before markup)</span>
              <span>{format(netTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Markup applied</span>
              <span>{markupPercent || 0}%</span>
            </div>
            <div className="flex justify-between items-center pt-1 border-t">
              <span className="font-semibold">Single supplement per traveller</span>
              <div className="text-right">
                <span className="text-lg font-bold text-primary">{format(grandTotal)}</span>
                {grandTotal > 0 && formatEquivalents(grandTotal, currency, exchangeRates as any) && (
                  <span className="block text-xs text-muted-foreground">
                    {formatEquivalents(grandTotal, currency, exchangeRates as any)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" className="gap-2" onClick={handleCopy}>
              <Copy className="h-4 w-4" /> Copy figure
            </Button>
            {savedAmount != null && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => persist(null)}
              >
                Remove from quote
              </Button>
            )}
          </div>
          <Button type="button" disabled={saving || grandTotal <= 0} onClick={() => persist(grandTotal)}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save to quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
